import { useEffect, useReducer, useState, type ReactNode } from 'react';
import {
  FACT_TYPE_FIELDS,
  importTargetableFields,
  IMPORT_PROSE_KINDS,
  IMPORT_TARGETABLE_FACT_TYPES,
  RECORD_FIELD_GROUPS,
  RELATION_RESOLVES,
  RELATION_TARGET_TYPES,
  RESOLUTION_LEVELS,
  SUBJECT_VOCABULARY_BY_VERTICAL,
  SEAT_PARTS,
  UNSUPPORTED_SHAPE_HINTS,
  correctionSaid,
  normalizeLocator,
  unsupportedColumns,
  type ColumnRef,
  type ColumnTarget,
  type FactType,
  type ImportPlan,
  type ImportPlanJoin,
  type ImportProfileSummary,
  type ImportShape,
  type PlanColumn,
  type PlanCorrection,
  type ResolutionLevel,
  type RowEventPerson,
  type SeatPart,
  type SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { AccountChoice } from '../accounts';
import { orgHasProtectionAccounts } from '../../lib/vertical';
import { usePreviewImport, usePutImportPlan } from '../../api/hooks';
import {
  ANCHOR_KIND_LABELS,
  BIN_REASON_LINES,
  COLUMN_TARGET_LABELS,
  FACT_TYPE_LABELS,
  factFieldLabel,
  IMPORT_GLANCE_HEAD,
  IMPORT_PLAN_BIN_HEAD,
  IMPORT_PLAN_COLUMNS_EDIT,
  IMPORT_PLAN_COLUMNS_HEAD,
  IMPORT_PLAN_COLUMNS_HIDE,
  IMPORT_PLAN_CORRECTION_REMOVE,
  IMPORT_PLAN_CORRECTIONS_HEAD,
  IMPORT_PLAN_CORRECTIONS_HINT,
  IMPORT_PLAN_DOCUMENT_PROSE,
  IMPORT_PLAN_EVERYONE_BECOMES,
  IMPORT_PLAN_EVERYTHING_HEAD,
  IMPORT_PLAN_EVERYTHING_OPEN,
  IMPORT_PLAN_EXCLUSIONS_HEAD,
  IMPORT_PLAN_GROUP_RULES_HEAD,
  IMPORT_PLAN_IDENTIFIERS_HEAD,
  IMPORT_PLAN_ISSUES_HEAD,
  IMPORT_PLAN_ISSUES_HINT,
  IMPORT_PLAN_JOINS_HEAD,
  IMPORT_PLAN_KEEP_EDITING,
  IMPORT_PLAN_MOVED,
  IMPORT_PLAN_NAME_HEAD,
  IMPORT_PLAN_NO_BIN_RULES,
  IMPORT_PLAN_NO_JOINS,
  IMPORT_PLAN_NO_OCCASION,
  IMPORT_PLAN_NO_RECORD_FIELDS,
  IMPORT_PLAN_OCCASION_DATED,
  IMPORT_PLAN_OCCASION_HEAD,
  IMPORT_PLAN_OCCASION_NAME,
  IMPORT_PLAN_OCCASION_NO_TIE,
  IMPORT_PLAN_OCCASION_PEOPLE_COLUMN,
  IMPORT_PLAN_OCCASION_PEOPLE_ROLE,
  IMPORT_PLAN_OCCASION_TIE,
  IMPORT_PLAN_OCCASION_UNDATED,
  IMPORT_PLAN_OCCASIONS_HEAD,
  IMPORT_PLAN_OCCASIONS_LEAD,
  IMPORT_PLAN_READ_AT_IMPORT,
  IMPORT_PLAN_READ_ONLY,
  IMPORT_PLAN_RECORD_FIELDS_HEAD,
  IMPORT_PLAN_RELOAD,
  IMPORT_PLAN_RESOLUTION_HEAD,
  IMPORT_PLAN_SAVE,
  IMPORT_PLAN_SAVED_RERUN,
  IMPORT_PLAN_SAVING,
  IMPORT_PLAN_SOURCE_HEAD,
  IMPORT_PLAN_TRACKED_ARE,
  IMPORT_PLAN_UNSUPPORTED_HEAD,
  IMPORT_PLAN_UNSUPPORTED_LEAD,
  IMPORT_PLAN_USER_MATCH_HEAD,
  IMPORT_PREVIEW_RUN,
  IMPORT_PREVIEW_RUNNING,
  IMPORT_PROSE_KIND_LABELS,
  IMPORT_SHAPE_SEND,
  IMPORT_SHAPE_SEND_HINT,
  IMPORT_TAB_NOT_YET,
  importPlanColumnsUnsure,
  importPlanCorrectionRemoveLabel,
  NAME_SPLIT_LABELS,
  OCCASION_KIND_LABELS,
  PLAN_CONFIDENCE_LABELS,
  planNameRuleWords,
  planTargetWords,
  RECORD_FIELD_GROUP_LABELS,
  RECORD_RULE_LABELS,
  RELATION_TARGET_LABELS,
  RESOLUTION_LEVEL_HINTS,
  RESOLUTION_LEVEL_LABELS,
  ROW_EVENT_ROLE_LABELS,
  SEAT_PART_LABELS,
  subjectStatusLabel,
  transformWords,
  UNSUPPORTED_SHAPE_LABELS,
  USER_MATCH_LABELS,
} from '../../lib/copy';
import { formatLoose, humanize, joinClauses, plural } from '../../lib/format';
import { PlanFindings } from './findings';
import { matchKey, planGlance } from './plan-glance';
import { shapeMailto } from './shape-mail';
import { EMPTY_PLAN_EDITOR, canRunPreview, planEditor } from './plan-state';
import { errorLine, validationSentence } from '../../api/errors';
import { Checkbox } from '../../components/ui/checkbox';
import { EventFold } from './event-fold';
import { ErrorLine } from '../../components/ui/error-line';
import { WorkingButton } from '../../components/ui/working-button';
import { useImportPage } from './session';
import { EmptyLine } from '../../components/ui/empty-line';
import { Section } from '../../components/ui/section';

/**
 * **Why the save was refused, in the server's own words where it has any.**
 *
 * `PUT /plan` refuses a plan the contract will not parse, and the contract now
 * refuses more than a shape: a plan that maps `employment.role` and no column at
 * all to `employment.org` describes a fact the catalog cannot write, and the
 * refusal says exactly that, naming the type and the word. `errorLine` maps
 * every `VALIDATION_FAILED` to "That was not accepted. Nothing was saved." and drops
 * `details.issues` on the floor — which is right for a code the reader cannot
 * act on, and wrong for this one, where the sentence *is* the repair
 * instruction and the control that makes it is on the screen they are standing
 * on.
 *
 * So the same two-step the rename, join and link doors take
 * (`validationSentence(error) ?? errorLine(error)`): the server's sentence when
 * there is one, the generic line when there is not. The first issue only, like
 * its siblings — a list of sentences where one belongs is a form's error
 * summary, and the office repairs one column at a time anyway.
 */
export const planSaveLine = (error: unknown): string =>
  validationSentence(error) ?? errorLine(error);

/**
 * **The Plan tab** (import note §1.7, §2.4) — the mapping as a document the
 * office reads, with the parts it can change made changeable and the rest read
 * only and saying so.
 *
 * What is editable here is what the note names: a column's **target**, the
 * **level** each group of people arrives at, and the **status** a subject gets.
 * Everything else — the record rule, the exclusions, the identifiers, the
 * occasion, a joined sheet — is rendered and not edited, because each is a
 * question about the file rather than about what to do with it, and the way to
 * change one is to say so in the conversation and let the planner rewrite the document.
 * A control that could write half a rule would be worse than a sentence saying
 * where the change is made.
 *
 * **Saving is not previewing.** `PUT /plan` writes a version and pushes the
 * phase back to `plan_ready`; nothing has been read against the new mapping
 * until the dry run has run again. So the save offers the preview rather than
 * running it, which is also the server's rule (`IMPORT_NOT_PREVIEWED`).
 */
export function ImportPlanTab() {
  const { importId, session } = useImportPage();
  const org = useOrg();
  const save = usePutImportPlan(importId);
  const preview = usePreviewImport(importId);

  /*
    The editor's whole state is one value and one reducer — `plan-state.ts`,
    where the three questions it answers are written down and asserted. What is
    left here is the wiring: every session read is fed in, every edit is fed in,
    and the save's answer carries the version it wrote.
  */
  const [state, dispatch] = useReducer(planEditor, EMPTY_PLAN_EDITOR);
  useEffect(() => {
    dispatch({ kind: 'session', plan: session.plan, version: session.plan_version });
  }, [session.plan, session.plan_version]);

  const draft = state.draft;

  /**
   * **What the dry run found about the file, where the mapping is** (§4.3,
   * 2026-09-14). The findings were drawn on the Import tab alone, which is where
   * they are confirmed — and the repair is here: "the plan may be reading names
   * from the wrong column" is a sentence about the name column, and the name
   * column is on this page. Read only, with a pointer per finding; the tick
   * stays with the commit, because that is what it is a decision about.
   *
   * **Not after the commit.** The server goes on publishing findings while the
   * plan version stands, and a committed session is one a failed commit leaves
   * `in_progress` — so the block would otherwise be on a page whose mapping is
   * read-only, over an Import tab that is reporting rather than asking. Both
   * ways out the lead-in names are gone by then: nothing here can be fixed, and
   * there is no box left to confirm.
   *
   * **The list is the session's**, published by the server only for the plan
   * version the dry run was run against — so a save from this tab, which
   * invalidates the session read, takes the block with it rather than leaving a
   * warning about a mapping that is gone.
   */
  const findings = session.committed ? [] : session.findings;

  /* Above the early return, because a finding is about the file and the stored
     plan rather than about the draft: the tab that has not built one yet is
     still the tab the mapping is repaired on. */
  if (!session.plan || !draft) {
    return (
      <div className="import-plan">
        <PlanFindings findings={findings} />
        <EmptyLine>{IMPORT_TAB_NOT_YET.plan}</EmptyLine>
      </div>
    );
  }

  const editable = session.state === 'in_progress' && !session.committed;
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[org.vertical];
  const edit = (plan: ImportPlan) => dispatch({ kind: 'edit', plan });

  /**
   * **A document maps one column**, and a table of one row whose only cell is a
   * pair of selects is a table pretending to be a document. Its controls ride
   * the labelled row below instead, which is what leaves a document plan with
   * three headings on it.
   */
  const single = draft.columns.length === 1 ? draft.columns[0]! : null;

  const resolution = (
    <PlanResolution
      plan={draft}
      editable={editable}
      statuses={vocabulary?.statuses ?? []}
      vertical={org.vertical}
      onChange={edit}
      single={single}
      onSingleColumn={(column) => edit({ ...draft, columns: [column] })}
    />
  );

  return (
    <div className="import-plan">
      {/* The plan moved under an edit in flight. Both ways out are named, and
          neither is taken for the office: the draft is theirs. */}
      {state.moved ? (
        <div className="section" aria-live="polite">
          <div className="src">{IMPORT_PLAN_MOVED}</div>
          <div className="layer-actions">
            <button
              type="button"
              className="quiet-action"
              onClick={() => dispatch({ kind: 'reload' })}
            >
              {IMPORT_PLAN_RELOAD}
            </button>
            <button
              type="button"
              className="quiet-action"
              onClick={() => dispatch({ kind: 'keep' })}
            >
              {IMPORT_PLAN_KEEP_EDITING}
            </button>
          </div>
        </div>
      ) : null}

      {/* Above the glance on purpose. The glance answers "what will this do to
          my people" and these are the reason to doubt the answer, so they are
          not a footnote to it. Why the list is what it is, above. */}
      <PlanFindings findings={findings} />

      {/* **Above the glance, because it is the first thing about the file**
          (security fold, 2026-09-22): one protection account for the file, not
          one per row. A spreadsheet is the roster of one engagement, and a
          per-row account would put the wall in a column a model mapped. The
          commit is refused without it, and it is asked here rather than at the
          end of a run. */}
      {orgHasProtectionAccounts(org) && !session.target_event ? (
        <div className="section">
          <fieldset disabled={!editable}>
            <AccountChoice
              active
              value={draft.account_id ?? ''}
              onChange={(id) => edit({ ...draft, account_id: id || null })}
            />
          </fieldset>
          {draft.account_id === null ? (
            <div className="field-note">
              Every person and organization this file writes is filed under one protection account.
              Choose one before importing.
            </div>
          ) : null}
        </div>
      ) : null}

      <PlanGlance
        plan={draft}
        profile={session.profile_summary}
        vertical={org.vertical}
        {...(session.target_event ? { eventName: session.target_event.display_name } : {})}
      />

      {/* **Above everything editable**, because it is not a decision anybody
          made: the plan met these columns, could say what they hold, and had
          nowhere to put them. Under the controls it would read as a footnote to
          a mapping the office wrote. */}
      <PlanUnsupported
        plan={draft}
        profile={session.profile_summary}
        orgName={org.name}
        description={session.description}
        filename={session.file.filename}
        container={session.file.container}
        shape={session.shape}
      />

      {/* **Under what the plan could not do and above what the office decides**,
          because a correction is neither: it is an exception they asked for by
          hand, and the mapping below is read over the corrected cells. Drawn
          only when there is one. */}
      <PlanCorrections plan={draft} editable={editable} onChange={edit} />

      {/* **Under the corrections it is nearly always about.** An issue is what
          became of one — the cell did not say what the correction said it said,
          so nothing was changed — and it reads as a note on the list above it
          rather than as a section of its own concern. The list is the session's
          and not the draft's: the dry run was run against the stored plan, and
          the server drops it the moment the plan moves. */}
      <PlanIssues issues={session.plan_issues} />

      {/* **A file of occasions gets its own section instead of the levels
          table**, because on such a file the level is not one answer over
          everybody: it belongs to the column, and `resolution` is read for no
          row of it. Showing both would put two controls on the page, one of
          which decides nothing. */}
      {draft.row_event ? (
        <PlanOccasions
          plan={draft}
          rowEvent={draft.row_event}
          editable={editable}
          statuses={vocabulary?.statuses ?? []}
          vertical={org.vertical}
          onChange={edit}
        />
      ) : (
        /* Not on a document: its one column's controls ride this section, and
           folding them would hide the file's only mapping under a label about
           levels. */
        <EventFold folded={session.target_event !== null && single === null}>
          {resolution}
        </EventFold>
      )}

      {/* Two columns or more is a table, folded under the glance that already
          says where each column goes; one is a line above. */}
      {draft.columns.length > 1 ? (
        <ColumnsFold columns={draft.columns}>
          <PlanColumns
            columns={draft.columns}
            editable={editable}
            profile={session.profile_summary}
            sheet={draft.source.sheet}
            attendeeList={session.target_event !== null}
            onChange={(index, column) =>
              edit({
                ...draft,
                columns: draft.columns.map((current, at) => (at === index ? column : current)),
              })
            }
          />
        </ColumnsFold>
      ) : null}

      <PlanEverything plan={draft} />

      {/* Save is offered when there is something to save and not otherwise: a
          draft that says exactly what the server already has is not a pending
          decision, and a button standing there says it is. */}
      {editable && (state.dirty || canRunPreview(state)) ? (
        <div className="layer-actions">
          {state.dirty ? (
            <WorkingButton
              label={IMPORT_PLAN_SAVE}
              workingLabel={IMPORT_PLAN_SAVING}
              working={save.isPending}
              onClick={() =>
                save.mutate(draft, {
                  onSuccess: (written) =>
                    dispatch({ kind: 'saved', plan: written.plan, version: written.version }),
                })
              }
            />
          ) : null}
          {canRunPreview(state) ? (
            <WorkingButton
              label={IMPORT_PREVIEW_RUN}
              workingLabel={IMPORT_PREVIEW_RUNNING}
              variant="act"
              working={preview.isPending}
              onClick={() =>
                preview.mutate(undefined, { onSuccess: () => dispatch({ kind: 'previewed' }) })
              }
            />
          ) : null}
        </div>
      ) : null}

      {canRunPreview(state) ? <div className="src">{IMPORT_PLAN_SAVED_RERUN}</div> : null}
      {save.isError ? <div className="src">{planSaveLine(save.error)}</div> : null}
      {preview.isError ? <ErrorLine error={preview.error} /> : null}
    </div>
  );
}

/* ————————————————————————— the glance ————————————————————————— */

/**
 * **The answer to the question the office actually has** — what will this do to
 * my people — in six or seven lines, above everything that can be changed.
 *
 * Computed rather than written: see `plan-glance.ts` for each line and why it
 * says what it says.
 */
export function PlanGlance({
  plan,
  profile,
  vertical,
  eventName,
}: {
  plan: ImportPlan;
  profile?: ImportProfileSummary;
  vertical: string;
  /** An attendee list's event, said first: where its people go is the question. */
  eventName?: string;
}) {
  return (
    <Section title={IMPORT_GLANCE_HEAD}>
      {planGlance(plan, profile, vertical, eventName).map((line) => (
        <div className="act-row" key={line.label}>
          <div className="act-name">{line.label}</div>
          <div className="act-value fill">{line.value}</div>
        </div>
      ))}
    </Section>
  );
}

/* ————————————————————————— what nothing can hold yet ————————————————————————— */

/**
 * **The columns the plan could describe and cannot write** (owner's ruling,
 * 2026-09-07), each with the planner's own sentence about it, and one way to
 * send the file's shape to somebody who could build the missing half.
 *
 * It exists because the alternative was worse than saying nothing: before this,
 * a column like a reservations sheet's `Guest Name` had exactly one legal
 * target, `ignore`, whose whole meaning is *we know what it is and are choosing
 * not to import it*. That prints a gap in the product as a decision the office
 * made, and leaves the file as the only place the gap is written down.
 *
 * **The columns are still editable below.** Nothing here is final: the office
 * may look at one and decide it is a detail to keep on the person after all, or
 * that it really is an ignore. The section is a statement about what the plan
 * did, not a lock on it — which is also why the count on the Import tab does not
 * stop the commit.
 *
 * Renders nothing at all on the ordinary plan, which has none of these.
 */
export function PlanUnsupported({
  plan,
  profile,
  orgName,
  description,
  filename,
  container,
  shape,
}: {
  plan: ImportPlan;
  profile?: ImportProfileSummary;
  orgName: string;
  description: string;
  filename: string;
  container: string;
  shape: ImportShape;
}) {
  const columns = unsupportedColumns(plan);
  if (columns.length === 0) return null;

  const samples = sampleIndex(profile, plan.source.sheet);
  /* Composed here rather than on the press, because it is an `href`: the office
     sees where it goes before they take it, their client opens it unsent, and
     nothing this page does sends anything. */
  const href = shapeMailto({
    orgName,
    description,
    filename,
    container,
    shape,
    plan,
    profile,
  });

  return (
    <div className="section import-unsupported">
      <h2>{IMPORT_PLAN_UNSUPPORTED_HEAD}</h2>
      <div className="src">{IMPORT_PLAN_UNSUPPORTED_LEAD}</div>
      <ul className="fact-lines">
        {columns.map((column) => (
          <li key={`${column.column.index}-${column.column.header}`}>
            {/* The column's own header in the app's field voice — the small-caps
                a `plan-control-label` carries — so the planner's sentence under
                it reads as the value rather than as a second heading. */}
            <div className="plan-control-label">{columnName(column.column)}</div>
            {/* The planner's sentence, whole: it is the entire value of the
                target and the thing the office is being asked to recognise. */}
            <div>{column.target.kind === 'unsupported' ? column.target.meaning : ''}</div>
            <div className="src">{(samples.get(column.column.index) ?? []).join(' · ')}</div>
          </li>
        ))}
      </ul>
      <div className="layer-actions">
        <a className="quiet-action" href={href}>
          {IMPORT_SHAPE_SEND}
        </a>
      </div>
      {/* **What is in the mail, said before it is opened.** They are about to
          send something about their members' file outside the office, and the
          one thing they need to know is that it is the shape and not the
          people. */}
      <div className="src">{IMPORT_SHAPE_SEND_HINT}</div>
    </div>
  );
}

/* ————————————————————————— cells read otherwise ————————————————————————— */

/**
 * **The cells the file gets wrong, read as the office said they should be**
 * (§4.4, 2026-09-15).
 *
 * Punta Brava's re-import: two rows carry member number `0074` because the
 * spreadsheet slipped, and Aaron Graft's is `0075`. The office could not say so
 * — editing the file replaces the bytes and drops every row answer with them
 * (§4.2), setting the row aside loses the person — so the fact now lives on the
 * plan, which is the document that says how the file is read.
 *
 * **Top-level, and not in the fold.** Everything in `PlanEverything` is the
 * plan's own reading of the file, read-only detail nobody asked for by hand. A
 * correction is the opposite: somebody typed a sentence and this is the list of
 * what it did. Folding it away would hide the one thing on the tab that is an
 * exception, and the exception is exactly what a second reader needs to see.
 *
 * **No empty-state sentence.** The read-only blocks each carry one because they
 * are always drawn and a blank block is a question ("did it find none, or did it
 * not look?"). This section is drawn only when there is something in it, so the
 * question never arises — and a heading over nothing would suggest a door the
 * office had not gone through.
 *
 * The row label comes from the locator and never the locator itself: `contacts:100`
 * is the parser's address, and *Row 100* is where the person is looking in their
 * own file.
 *
 * **And a way off the plan** (the 2026-09-15 review). This section shipped
 * read-only, which was a dead end and not a restraint: a correction stops
 * applying the moment the file is replaced and the row moves under it, the
 * commit then refuses on it — in a sentence that says to fix or remove it *on
 * the Plan tab* — and the tab had nothing on it that could. The planner was no
 * way out either, being told to carry every correction forward. So each row
 * carries the one control that gets it off: it edits the draft, exactly as a
 * target select does, and the Save button below writes the version. Nothing
 * saves on the press, which is what makes the way to undo one "reload the page"
 * rather than a dialog nobody wants twice.
 */
export function PlanCorrections({
  plan,
  editable = false,
  onChange,
}: {
  plan: ImportPlan;
  /** The tab's own rule: a committed or finished session is read all the way down. */
  editable?: boolean;
  onChange?: (plan: ImportPlan) => void;
}) {
  const corrections = plan.corrections;
  if (corrections.length === 0) return null;

  return (
    <Section title={IMPORT_PLAN_CORRECTIONS_HEAD}>
      {/* Text on the page, not a `title`: what the office needs to know is that
          the file on disk still says 0074, and a tooltip is invisible to a
          keyboard and unreliable on a touch screen. */}
      <div className="src">{IMPORT_PLAN_CORRECTIONS_HINT}</div>
      {corrections.map((correction) => {
        const where = correctionRow(correction.locator, plan.source.sheet);
        const column = columnName(correction.column);
        return (
          /* The contract refuses two corrections naming one cell, so the row and
             the column are a key on their own. */
          <div key={`${correction.locator}-${correction.column.index}`}>
            <div className="act-row">
              <div className="act-name">{`${where}, ${correction.row_name} · ${column}`}</div>
              {/* `correctionSaid` and not a phrase built here: how an empty cell is
                  spelled is the contract's, so this line and the remark the
                  executor writes on the record cannot drift apart. */}
              <div className="act-value fill">{correctionSaid(correction)}</div>
              {editable && onChange ? (
                <button
                  type="button"
                  className="quiet-action"
                  /* The visible word is one word on six rows that look alike, so
                     what is read out names the row and the column it is on. */
                  aria-label={importPlanCorrectionRemoveLabel(where, column)}
                  onClick={() => onChange(withoutCorrection(plan, correction))}
                >
                  {IMPORT_PLAN_CORRECTION_REMOVE}
                </button>
              ) : null}
            </div>
            {/* The office's own sentence, under the change it accounts for. */}
            <div className="src">{correction.reason}</div>
          </div>
        );
      })}
    </Section>
  );
}

/**
 * **The plan without that one correction**, and a pure function because that is
 * the whole of what the Remove control decides — the suite has no DOM, and this
 * is the part worth asserting.
 *
 * **Keyed on the cell and not on the object.** A draft is re-seeded from the
 * session on every poll, so the correction handed to a click handler minted one
 * render ago is a different object from the one in the plan being edited now,
 * and an identity test would quietly remove nothing. The cell is what the
 * contract says a correction *is* one of — the locator and the column's index,
 * one correction each — so it is what this removes by, through
 * {@link normalizeLocator}, for the reason the contract's own pair check uses
 * it: `contacts:0100` and `contacts:100` are one row.
 */
export function withoutCorrection(plan: ImportPlan, correction: PlanCorrection): ImportPlan {
  const cell = (one: PlanCorrection) => `${normalizeLocator(one.locator)}\u0000${one.column.index}`;
  const gone = cell(correction);
  return { ...plan, corrections: plan.corrections.filter((held) => cell(held) !== gone) };
}

/**
 * **Where a correction's row is, in the file's own words.**
 *
 * A locator is `<sheet>:<row>` — the executor's address, split on the last `:`
 * followed by digits, because a sheet may have one in its name and the row
 * number never does. The sheet is named only when it is not the one the plan
 * reads: on a single-sheet file, and on a CSV, which has no sheet at all, *Row
 * 100 of contacts* would be telling somebody which sheet of a one-sheet file to
 * look at. A locator that does not parse is printed whole rather than guessed
 * at: an address nobody can read is better than a row number that is wrong.
 */
const correctionRow = (locator: string, sheet: string | undefined): string => {
  const at = /^(.*):(\d+)$/.exec(locator);
  if (!at) return locator;
  const [, on, row] = at;
  return sheet === undefined || on === sheet ? `Row ${row}` : `Row ${row} of ${on}`;
};

/**
 * **What the dry run could not do with the plan itself** (§4.4, 2026-09-15).
 *
 * `AppliedPlan.issues` has always existed and nothing published it: the plan
 * naming a sheet the file does not have was a sentence the executor built and
 * dropped on the way to the wire. A correction that did not apply is the first
 * of those the office has to see — it is the one thing on this tab they asked
 * for by hand, and a correction silently not made is worse than one refused —
 * so `ImportSession.plan_issues` opened the channel and every plan-level
 * problem comes down it.
 *
 * **The sentences are the server's, whole.** Each one already names the row,
 * the person, the column and what the cell actually says; rebuilding it here
 * would be a second place for the same words to drift. So the block is a
 * heading, the hint, and one line per sentence.
 *
 * **Drawn only where there is one**, for the reason {@link PlanCorrections} is:
 * a heading over an empty list reads as a question that was never asked.
 */
export function PlanIssues({ issues }: { issues: readonly string[] }) {
  if (issues.length === 0) return null;

  return (
    <Section title={IMPORT_PLAN_ISSUES_HEAD}>
      <div className="src">{IMPORT_PLAN_ISSUES_HINT}</div>
      {issues.map((issue, index) => (
        /* The sentence is the only thing here and two dry runs can find the
           same problem twice over, so the position is the key. */
        <div className="act-row" key={`${index}-${issue}`}>
          <div className="act-name">{issue}</div>
        </div>
      ))}
    </Section>
  );
}

/* ————————————————————————— everything else in the plan ————————————————————————— */

/**
 * **The read-only half, folded** (§1.7).
 *
 * Eight small-caps sections stood between the glance and the two controls that
 * do something, each carrying its own copy of the sentence saying it could not
 * be edited: eight headings and eight refusals above one select. The rule has
 * not changed and none of the detail has gone — it is one fold, closed on
 * arrival, with the sentence said once at the foot of it.
 */
export function PlanEverything({ plan }: { plan: ImportPlan }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="section">
      <button
        type="button"
        className="section-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? IMPORT_PLAN_EVERYTHING_OPEN : IMPORT_PLAN_EVERYTHING_HEAD}
      </button>
      {open ? (
        <div className="plan-everything">
          <PlanSource plan={plan} />
          <PlanIdentity plan={plan} />
          <PlanExclusions plan={plan} />
          <PlanIdentifiers plan={plan} />
          <PlanRecordFields plan={plan} />
          <PlanBin plan={plan} />
          <PlanJoins plan={plan} />
          <PlanOccasion plan={plan} />
          <div className="src">{IMPORT_PLAN_READ_ONLY}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * **The column table, closed on arrival** (2026-09-29). A board of 68 columns
 * was 68 rows of kind, fact type, part and group before the office reached the
 * buttons, and the glance above already says which columns become facts, notes,
 * details and relationships. The fold's label carries how many columns the
 * planner was least sure of, the one reason to open it unprompted.
 */
export function ColumnsFold({
  columns,
  children,
}: {
  columns: readonly PlanColumn[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const unsure = columns.filter((column) => column.confidence === 'low').length;
  return (
    <div className="section">
      <button
        type="button"
        className="section-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open
          ? IMPORT_PLAN_COLUMNS_HIDE
          : joinClauses([
              IMPORT_PLAN_COLUMNS_EDIT,
              unsure > 0 ? importPlanColumnsUnsure(unsure) : null,
            ])}
      </button>
      {open ? children : null}
    </div>
  );
}

/* ————————————————————————— the column table ————————————————————————— */

/**
 * **The six kinds a column can become**, as the select offers them.
 *
 * `unsupported` is on the list rather than off it, and the ruling says why: the
 * office may look at one of these and decide it is a detail to keep on the
 * person, or an ignore after all, and a target they can be shown and cannot
 * change is a target they have to ask somebody about. It is also how one gets
 * *back* to unsupported after a change they did not mean.
 */
const TARGET_KINDS = [
  'fact',
  'record_field',
  'prose',
  'relation',
  'ignore',
  'unsupported',
] as const;

/** An attendee list's kinds: the six, and the seat, which only it keeps. */
const ATTENDEE_TARGET_KINDS = [...TARGET_KINDS, 'seat'] as const;

/** The type a fresh fact target lands on, and the first field the catalog gives it. */
const DEFAULT_FACT_TYPE: FactType = 'employment';

/**
 * **A detail's name, from the column it comes from** — "Wire Date" is kept as
 * `wire_date`. The default was once the word `field` for every column, and a
 * plan that turned seven columns into details wrote all seven to one detail.
 */
export const recordFieldKeyFor = (header: string): string =>
  header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'detail';

/**
 * **A target of the chosen kind, with the least that kind needs to parse.**
 *
 * Switching kind in the select cannot carry the old kind's fields across — a
 * `fact`'s `field` means nothing to a `relation` — so each arm is built from
 * scratch, with values that are legal rather than blank: a plan that will not
 * parse is a plan the office cannot save, and an empty required string is the
 * commonest way to write one.
 *
 * The fact arm's default used to be `{type: 'employment', field: 'employer'}`,
 * which is not a field `employment` has — so the very first thing the control
 * offered was a target that could not be saved, and the refusal named a `field`
 * the office had never typed. It is the catalog's own first field now, which for
 * `employment` is `org`.
 */
function emptyTarget(kind: (typeof ATTENDEE_TARGET_KINDS)[number], header: string): ColumnTarget {
  switch (kind) {
    case 'fact':
      return factTarget(DEFAULT_FACT_TYPE);
    case 'record_field':
      return { kind: 'record_field', key: recordFieldKeyFor(header), group: 'membership' };
    case 'prose':
      return { kind: 'prose', prose_kind: 'note', extract: true };
    case 'relation':
      return {
        kind: 'relation',
        type: 'family_relation',
        relation: 'spouse',
        resolve: 'satellite_person',
      };
    case 'ignore':
      return { kind: 'ignore', reason: 'not needed' };
    /* A sentence rather than a blank, for the reason every other arm here has
       one: `meaning` is required, and an empty required string is the commonest
       way to build a plan the save then refuses. */
    case 'unsupported':
      return { kind: 'unsupported', meaning: 'nothing here can hold this yet' };
    case 'seat':
      return { kind: 'seat', part: 'whole' };
  }
}

/**
 * **A fact target for a type, on that type's own first field.**
 *
 * Changing the type has to move the field with it, and clear the transforms
 * besides: a `split_list` written for a column of club names says nothing about
 * a date, and the pair `{type: 'residence', field: 'org'}` does not parse at
 * all. Both were carried across before, so the select could be used to build a
 * plan the save then refused.
 */
const factTarget = (type: FactType): ColumnTarget => ({
  kind: 'fact',
  type,
  field: FACT_TYPE_FIELDS[type][0] as string,
});

export function PlanColumns({
  columns,
  editable,
  profile,
  sheet,
  attendeeList = false,
  onChange,
}: {
  columns: readonly PlanColumn[];
  editable: boolean;
  /** The file is an event's attendee list, which may keep a seat. */
  attendeeList?: boolean;
  /** What the file holds — the samples column, so a mapping can be checked by eye. */
  profile?: ImportProfileSummary;
  /** Which sheet the plan reads, when the file has more than one. */
  sheet?: string;
  onChange: (index: number, column: PlanColumn) => void;
}) {
  const samples = sampleIndex(profile, sheet);

  return (
    <Section title={IMPORT_PLAN_COLUMNS_HEAD}>
      {columns.length === 0 ? (
        <EmptyLine>No columns are mapped.</EmptyLine>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Column</th>
              <th scope="col">What is in it</th>
              <th scope="col">What it becomes</th>
              <th scope="col">How sure</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((column, index) => {
              const header = columnName(column.column);
              return (
                <tr key={`${column.column.index}-${column.column.header}`}>
                  <td>
                    {header}
                    <div className="src">column {column.column.index}</div>
                  </td>
                  {/* Three cells of the file itself, beside the mapping written
                      for them. It is the whole of what makes a plan checkable by
                      eye: "Club Affiliations → association membership" is a
                      guess until the cells beside it read like club names. */}
                  <td className="quiet-cell">
                    {(samples.get(column.column.index) ?? []).join(' · ')}
                  </td>
                  <td>
                    <div>{planTargetWords(column.target)}</div>
                    {/* What the executor does to the cell before it files it,
                        in words — a plan is a document a person reads, and two
                        transforms on one column is the case §2.2 was written
                        for. */}
                    {'transforms' in column.target && column.target.transforms ? (
                      <div className="src">
                        {column.target.transforms.map(transformWords).join(', then ')}
                      </div>
                    ) : null}
                    {column.note ? <div className="src">{column.note}</div> : null}
                    {editable ? (
                      <TargetEditor
                        target={column.target}
                        /* Every select in the row is named for **this column**,
                           not for the table. A page of 71 rows whose controls
                           all announce "Which fact" is a page a screen reader
                           cannot navigate. */
                        column={header}
                        attendeeList={attendeeList}
                        onChange={(target) => onChange(index, { ...column, target })}
                      />
                    ) : null}
                  </td>
                  <td className="quiet-cell">{PLAN_CONFIDENCE_LABELS[column.confidence]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Section>
  );
}

/** A column's own words, or its number when the file gave it none. */
const columnName = (ref: ColumnRef): string =>
  ref.header.length > 0 ? ref.header : `column ${ref.index}`;

/** The samples for each column of the sheet this plan reads, by column index. */
function sampleIndex(
  profile: ImportProfileSummary | undefined,
  sheet: string | undefined,
): Map<number, readonly string[]> {
  const chosen =
    profile?.sheets.find((candidate) => candidate.name === sheet) ?? profile?.sheets[0];
  return new Map((chosen?.columns ?? []).map((column) => [column.index, column.samples]));
}

/**
 * The controls for one target: the kind, and the minimum each kind needs said
 * about it. A routed prose kind — one chosen per cell by what it starts with —
 * is deliberately not editable here: it is a list of prefixes with a kind and a
 * read flag each, which is a table of its own and belongs to the planner.
 */
export function TargetEditor({
  target,
  column,
  attendeeList = false,
  onChange,
}: {
  target: ColumnTarget;
  /** The column these controls are about — every accessible name says it. */
  column: string;
  /** Offer the seat, which only an attendee list keeps. */
  attendeeList?: boolean;
  onChange: (target: ColumnTarget) => void;
}) {
  const named = (what: string) => `${what}: ${column}`;

  return (
    <div className="target-editor">
      <select
        className="control"
        aria-label={named('What this column becomes')}
        value={target.kind}
        onChange={(event) => onChange(emptyTarget(event.target.value as 'fact', column))}
      >
        {(attendeeList ? ATTENDEE_TARGET_KINDS : TARGET_KINDS).map((kind) => (
          <option key={kind} value={kind}>
            {COLUMN_TARGET_LABELS[kind]}
          </option>
        ))}
      </select>

      {target.kind === 'fact' ? (
        <>
          <select
            className="control"
            aria-label={named('Which fact')}
            value={target.type}
            /* The type carries the field with it and drops the transforms: the
               pair has to be one the catalog knows, and a `split_list` written
               for club names says nothing about a date. */
            onChange={(event) => onChange(factTarget(event.target.value as FactType))}
          >
            {/* The catalog's types minus the ones no column can be pointed at —
                `org_relation` has an entity on both sides, and a column supplies
                one value against one person. Offering it would be a select that
                builds a plan the save refuses. */}
            {IMPORT_TARGETABLE_FACT_TYPES.map((type) => (
              <option key={type} value={type}>
                {FACT_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          {/* A select and not a text box, off the catalog's own vocabulary: the
              field is one of a handful of closed words per type, and a free
              string is how `field: 'employer'` — which `employment` does not
              have — got as far as the save. */}
          <select
            className="control"
            aria-label={named('Which part of it')}
            value={target.field}
            onChange={(event) => onChange({ ...target, field: event.target.value })}
          >
            {importTargetableFields(target.type).map((field) => (
              <option key={field} value={field}>
                {factFieldLabel(field)}
              </option>
            ))}
          </select>
        </>
      ) : null}

      {target.kind === 'record_field' ? (
        <>
          <input
            className="control"
            type="text"
            aria-label={named('What the detail is called')}
            value={target.key}
            onChange={(event) => onChange({ ...target, key: event.target.value })}
          />
          <select
            className="control"
            aria-label={named('Where it belongs')}
            value={target.group}
            onChange={(event) =>
              onChange({ ...target, group: event.target.value as typeof target.group })
            }
          >
            {RECORD_FIELD_GROUPS.map((group) => (
              <option key={group} value={group}>
                {RECORD_FIELD_GROUP_LABELS[group]}
              </option>
            ))}
          </select>
        </>
      ) : null}

      {target.kind === 'prose' ? (
        typeof target.prose_kind === 'string' ? (
          <>
            <select
              className="control"
              aria-label={named('What the notes are')}
              value={target.prose_kind}
              onChange={(event) =>
                onChange({ ...target, prose_kind: event.target.value as 'note' })
              }
            >
              {IMPORT_PROSE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {IMPORT_PROSE_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
            <Checkbox
              className="near-match"
              checked={target.extract}
              onChange={(extract) => onChange({ ...target, extract })}
            >
              {IMPORT_PLAN_READ_AT_IMPORT}
            </Checkbox>
          </>
        ) : (
          <div className="src">{IMPORT_PLAN_READ_ONLY}</div>
        )
      ) : null}

      {target.kind === 'relation' ? (
        <>
          <select
            className="control"
            aria-label={named('Which kind of relationship')}
            value={target.type}
            onChange={(event) => {
              const type = event.target.value as (typeof RELATION_TARGET_TYPES)[number];
              /* A `family_relation` must say which relation it is and a
                 `social_tie` must not carry one at all — the contract refuses
                 either mistake, so the switch rebuilds both halves. */
              return onChange(
                type === 'family_relation'
                  ? { ...target, type, relation: target.relation ?? 'spouse' }
                  : { ...target, type, relation: undefined, context: target.context ?? 'a tie' },
              );
            }}
          >
            {RELATION_TARGET_TYPES.map((type) => (
              <option key={type} value={type}>
                {RELATION_TARGET_LABELS[type]}
              </option>
            ))}
          </select>
          {target.type === 'family_relation' ? (
            <input
              className="control"
              type="text"
              aria-label={named('Which relation')}
              value={target.relation ?? ''}
              onChange={(event) => onChange({ ...target, relation: event.target.value })}
            />
          ) : (
            <input
              className="control"
              type="text"
              aria-label={named('What the tie is')}
              value={target.context ?? ''}
              onChange={(event) => onChange({ ...target, context: event.target.value })}
            />
          )}
          <select
            className="control"
            aria-label={named('How the other person is found')}
            value={target.resolve}
            onChange={(event) =>
              onChange({ ...target, resolve: event.target.value as typeof target.resolve })
            }
          >
            {RELATION_RESOLVES.map((resolve) => (
              <option key={resolve} value={resolve}>
                {resolve === 'in_file_name'
                  ? 'Someone else on this file'
                  : 'A person kept for the name'}
              </option>
            ))}
          </select>
        </>
      ) : null}

      {target.kind === 'ignore' ? (
        <input
          className="control"
          type="text"
          aria-label={named('Why it is left out')}
          value={target.reason}
          onChange={(event) => onChange({ ...target, reason: event.target.value })}
        />
      ) : null}

      {target.kind === 'unsupported' ? (
        <>
          <input
            className="control"
            type="text"
            aria-label={named('What this column holds')}
            value={target.meaning}
            onChange={(event) => onChange({ ...target, meaning: event.target.value })}
          />
          {/* Optional on the contract and optional here: the empty option is
              the honest answer, and a select with no way back to it would make
              the first accidental press permanent. */}
          <select
            className="control"
            aria-label={named('Where it would belong')}
            value={target.shape_hint ?? ''}
            onChange={(event) =>
              onChange({
                ...target,
                shape_hint: (event.target.value || undefined) as typeof target.shape_hint,
              })
            }
          >
            <option value="">Not said</option>
            {UNSUPPORTED_SHAPE_HINTS.map((hint) => (
              <option key={hint} value={hint}>
                {UNSUPPORTED_SHAPE_LABELS[hint]}
              </option>
            ))}
          </select>
        </>
      ) : null}

      {target.kind === 'seat' ? (
        <select
          className="control"
          aria-label={named('Which part of the seat')}
          value={target.part}
          onChange={(event) => onChange({ kind: 'seat', part: event.target.value as SeatPart })}
        >
          {SEAT_PARTS.map((part) => (
            <option key={part} value={part}>
              {SEAT_PART_LABELS[part]}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

/* ————————————————————————— rows that are occasions ————————————————————————— */

/**
 * **A file whose rows are occasions** (owner's ruling, 2026-09-07) — the mapping
 * read out, and the one thing on it the office changes.
 *
 * The **mapping is read only** for the reason every other structural half of the
 * plan is: which column holds the guests and which holds the date is a fact
 * about the file, and a select over it would be offering somebody the chance to
 * make the plan wrong about the file in front of them. What is editable is what
 * those people *become* — the level per role, and the status where the level is
 * a subject — which is §1.3's own decision asked once per column instead of once
 * per file, because the member is somebody the club tracks and the guest is
 * somebody it now knows of.
 *
 * It stands in for {@link PlanResolution} rather than beside it: such a plan
 * reads `resolution` for no row, so the default that table edits would decide
 * nothing at all.
 */
export function PlanOccasions({
  plan,
  rowEvent,
  editable,
  statuses,
  vertical,
  onChange,
}: {
  plan: ImportPlan;
  rowEvent: NonNullable<ImportPlan['row_event']>;
  editable: boolean;
  statuses: readonly SubjectStatus[];
  vertical: string;
  onChange: (plan: ImportPlan) => void;
}) {
  const setPerson = (at: number, person: RowEventPerson) =>
    onChange({
      ...plan,
      row_event: {
        ...rowEvent,
        people: rowEvent.people.map((current, on) => (on === at ? person : current)),
      },
    });

  const name =
    typeof rowEvent.occasion.name === 'string'
      ? rowEvent.occasion.name
      : columnName(rowEvent.occasion.name);

  return (
    <Section title={IMPORT_PLAN_OCCASIONS_HEAD}>
      <div className="src">{IMPORT_PLAN_OCCASIONS_LEAD}</div>

      <div className="plan-block">
        <div className="act-row">
          <div className="act-name">{IMPORT_PLAN_OCCASION_NAME}</div>
          <div className="act-value fill">{name}</div>
        </div>
        <div className="act-row">
          <div className="act-name">{IMPORT_PLAN_OCCASION_DATED}</div>
          <div className="act-value fill">
            {rowEvent.occasion.date
              ? columnName(rowEvent.occasion.date)
              : IMPORT_PLAN_OCCASION_UNDATED}
          </div>
        </div>
        <div className="act-row">
          <div className="act-name">{IMPORT_PLAN_OCCASION_TIE}</div>
          <div className="act-value fill">
            {rowEvent.tie ? rowEvent.tie.context : IMPORT_PLAN_OCCASION_NO_TIE}
          </div>
        </div>
      </div>

      <table className="table plan-rules">
        <thead>
          <tr>
            <th>{IMPORT_PLAN_OCCASION_PEOPLE_COLUMN}</th>
            <th>{IMPORT_PLAN_OCCASION_PEOPLE_ROLE}</th>
            <th>{IMPORT_PLAN_EVERYONE_BECOMES}</th>
            <th>{IMPORT_PLAN_TRACKED_ARE}</th>
          </tr>
        </thead>
        <tbody>
          {rowEvent.people.map((person, at) => (
            <tr key={`${person.column.index}-${person.role}`}>
              <td>{columnName(person.column)}</td>
              <td className="quiet-cell">{ROW_EVENT_ROLE_LABELS[person.role]}</td>
              <td>
                <select
                  className="control"
                  aria-label={`${columnName(person.column)} ${IMPORT_PLAN_EVERYONE_BECOMES}`}
                  value={person.level}
                  disabled={!editable}
                  onChange={(event) =>
                    setPerson(at, {
                      ...person,
                      level: event.target.value as ResolutionLevel,
                    })
                  }
                >
                  {RESOLUTION_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {RESOLUTION_LEVEL_LABELS[level]}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                {/* **A status belongs to a subject and to nothing else**, so the
                    cell is empty rather than disabled on the other two levels: a
                    greyed select says there is an answer here somebody could
                    reach, and there is not. */}
                {person.level !== 'subject' || statuses.length === 0 ? (
                  <span className="quiet-cell">—</span>
                ) : (
                  <select
                    className="control"
                    aria-label={`${columnName(person.column)} ${IMPORT_PLAN_TRACKED_ARE}`}
                    value={person.status ?? plan.status.default}
                    disabled={!editable}
                    onChange={(event) =>
                      setPerson(at, {
                        ...person,
                        status: event.target.value as SubjectStatus,
                      })
                    }
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {subjectStatusLabel(vertical, status) ?? status}
                      </option>
                    ))}
                  </select>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

/* ————————————————————————— what each person becomes ————————————————————————— */

/**
 * **The level and status rules** (§1.3) — the default each person arrives at,
 * and the per-group and per-column exceptions.
 *
 * The **match** is read only and the **outcome** is editable: which group a rule
 * catches is a fact about the file, and what happens to that group is the
 * office's call. That is the whole of what this table lets somebody change, and
 * it is the change §1.3 says they make.
 */
export function PlanResolution({
  plan,
  editable,
  statuses,
  vertical,
  onChange,
  single,
  onSingleColumn,
}: {
  plan: ImportPlan;
  editable: boolean;
  statuses: readonly SubjectStatus[];
  vertical: string;
  onChange: (plan: ImportPlan) => void;
  /** The one column a document maps, whose controls ride this section. */
  single?: PlanColumn | null;
  onSingleColumn?: (column: PlanColumn) => void;
}) {
  const levelSelect = (
    value: ResolutionLevel,
    label: string,
    set: (level: ResolutionLevel) => void,
  ) => (
    <select
      className="control"
      aria-label={label}
      value={value}
      disabled={!editable}
      onChange={(event) => set(event.target.value as ResolutionLevel)}
    >
      {RESOLUTION_LEVELS.map((level) => (
        <option key={level} value={level}>
          {RESOLUTION_LEVEL_LABELS[level]}
        </option>
      ))}
    </select>
  );

  const statusSelect = (
    value: SubjectStatus,
    label: string,
    set: (status: SubjectStatus) => void,
  ) =>
    statuses.length === 0 ? (
      <span className="quiet-cell">{value}</span>
    ) : (
      <select
        className="control"
        aria-label={label}
        value={value}
        disabled={!editable}
        onChange={(event) => set(event.target.value as SubjectStatus)}
      >
        {statuses.map((status) => (
          <option key={status} value={status}>
            {subjectStatusLabel(vertical, status) ?? status}
          </option>
        ))}
      </select>
    );

  const rules = groupRules(plan);

  return (
    <Section title={IMPORT_PLAN_RESOLUTION_HEAD}>
      {/* **The two controls, on one line, each said out loud.** They were an
          unlabelled pair inside a row called "Everyone, unless a rule says
          otherwise", which named the row and neither control — so which select
          was the level and which the status was something you worked out by
          opening one. */}
      <div className="plan-controls">
        <label className="plan-control">
          <span className="plan-control-label">{IMPORT_PLAN_EVERYONE_BECOMES}</span>
          {levelSelect(plan.resolution.default, IMPORT_PLAN_EVERYONE_BECOMES, (level) =>
            onChange({ ...plan, resolution: { ...plan.resolution, default: level } }),
          )}
        </label>
        <label className="plan-control">
          <span className="plan-control-label">{IMPORT_PLAN_TRACKED_ARE}</span>
          {statusSelect(plan.status.default, IMPORT_PLAN_TRACKED_ARE, (status) =>
            onChange({ ...plan, status: { ...plan.status, default: status } }),
          )}
        </label>
      </div>

      {/* **The hint is text on the page, not a `title`.** A tooltip is invisible
          to a keyboard, unreliable on a touch screen and read by only some
          screen readers — and what it carries is the whole difference between
          the three levels: what "Known" *means* is the reason somebody picks
          it. */}
      <div className="src">{RESOLUTION_LEVEL_HINTS[plan.resolution.default]}</div>

      {/* **One row per group, not one per key.** The level rules and the status
          rules were two lists of rows, so a group that had both appeared twice
          under the same name with a different control on each — which reads as
          two rules about one group rather than as one group's own answer. */}
      {rules.length > 0 ? (
        <table className="table plan-rules">
          <thead>
            <tr>
              <th scope="col">{IMPORT_PLAN_GROUP_RULES_HEAD}</th>
              <th scope="col">{IMPORT_PLAN_EVERYONE_BECOMES}</th>
              <th scope="col">{IMPORT_PLAN_TRACKED_ARE}</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.key}>
                <td>{rule.label}</td>
                <td>
                  {rule.level === null ? (
                    <span className="quiet-cell">—</span>
                  ) : (
                    levelSelect(rule.level, `What ${rule.equals} becomes`, (level) =>
                      onChange({
                        ...plan,
                        resolution: {
                          ...plan.resolution,
                          rules: plan.resolution.rules.map((current, at) =>
                            at === rule.levelAt ? { ...current, level } : current,
                          ),
                        },
                      }),
                    )
                  )}
                </td>
                <td>
                  {rule.status === null ? (
                    <span className="quiet-cell">—</span>
                  ) : (
                    statusSelect(rule.status, `The status ${rule.equals} gets`, (status) =>
                      onChange({
                        ...plan,
                        status: {
                          ...plan.status,
                          rules: plan.status.rules.map((current, at) =>
                            at === rule.statusAt ? { ...current, status } : current,
                          ),
                        },
                      }),
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {/* A document's one column, as a line rather than as a table of one. The
          words are there whether or not it can be changed: a committed session
          hides the columns table, and this is the only place its one mapping is
          said. */}
      {single ? (
        <div className="plan-controls">
          <div className="plan-control">
            <span className="plan-control-label">{IMPORT_PLAN_DOCUMENT_PROSE}</span>
            <div className="act-value">{planTargetWords(single.target)}</div>
            {editable && onSingleColumn ? (
              <TargetEditor
                target={single.target}
                column={columnName(single.column)}
                onChange={(target) => onSingleColumn({ ...single, target })}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * **The rules, one row per group.**
 *
 * A level rule and a status rule about the same group are one exception in the
 * office's head — "the resigned are contacts I still track" — so they are one
 * row here, and the two `onChange`s below write back into whichever of the two
 * lists the cell came from. Ordered by the level rules first and then whatever
 * only the status rules mention, which keeps the order the planner wrote.
 */
export interface GroupRuleRow {
  readonly key: string;
  readonly label: string;
  /** The group or value the rule matches, for the controls' accessible names. */
  readonly equals: string;
  readonly level: ResolutionLevel | null;
  readonly status: SubjectStatus | null;
  readonly levelAt: number | null;
  readonly statusAt: number | null;
}

export function groupRules(plan: ImportPlan): GroupRuleRow[] {
  const rows = new Map<string, GroupRuleRow>();

  plan.resolution.rules.forEach((rule, at) => {
    const key = matchKey(rule);
    rows.set(key, {
      key,
      label: planMatchWords(rule),
      equals: rule.equals,
      level: rule.level,
      status: null,
      levelAt: at,
      statusAt: null,
    });
  });

  plan.status.rules.forEach((rule, at) => {
    const key = matchKey(rule);
    const held = rows.get(key);
    rows.set(key, {
      key,
      label: planMatchWords(rule),
      equals: rule.equals,
      level: held?.level ?? null,
      status: rule.status,
      levelAt: held?.levelAt ?? null,
      statusAt: at,
    });
  });

  return [...rows.values()];
}

/* ————————————————————————— the read-only halves ————————————————————————— */

/** What the file holds that is not a person, and how much of it (§1.8). */
export function PlanExclusions({ plan }: { plan: ImportPlan }) {
  const exclusions = plan.source.exclusions;
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_EXCLUSIONS_HEAD}</div>
      {exclusions.length === 0 ? (
        <EmptyLine>Every row of this file stands for a person.</EmptyLine>
      ) : (
        exclusions.map((exclusion, index) => (
          <div className="act-row" key={`${exclusion.rule}-${index}`}>
            <div className="act-name">{exclusion.detail}</div>
            <div className="act-value fill">{plural(exclusion.count, 'row', 'rows')}</div>
          </div>
        ))
      )}
    </div>
  );
}

/** How two rows are known to be one person, or two (§1.5). */
export function PlanIdentifiers({ plan }: { plan: ImportPlan }) {
  const identifiers = plan.identity.identifiers;
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_IDENTIFIERS_HEAD}</div>
      {identifiers.length === 0 ? (
        <EmptyLine>By name alone. This file carries nothing that identifies a person.</EmptyLine>
      ) : (
        identifiers.map((identifier, index) => (
          <div className="act-row" key={`${identifier.column.index}-${index}`}>
            <div className="act-name">{columnName(identifier.column)}</div>
            <div className="act-value fill">
              {joinClauses([
                ANCHOR_KIND_LABELS[identifier.anchor],
                identifier.namespace ?? null,
                identifier.transforms
                  ? identifier.transforms.map(transformWords).join(', then ')
                  : null,
              ])}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** Whether the file is an occasion everybody on it took part in (§1.3a). */
export function PlanOccasion({ plan }: { plan: ImportPlan }) {
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_OCCASION_HEAD}</div>
      {plan.occasion === null ? (
        <EmptyLine>{IMPORT_PLAN_NO_OCCASION}</EmptyLine>
      ) : (
        <div className="act-row">
          <div className="act-name">{plan.occasion.name}</div>
          <div className="act-value fill">
            {joinClauses([
              OCCASION_KIND_LABELS[plan.occasion.kind],
              plan.occasion.date ? formatLoose(plan.occasion.date) : null,
              'written about everyone on the file',
            ])}
          </div>
        </div>
      )}
    </div>
  );
}

/* ————————————————————————— the rest of the document ————————————————————————— */

/**
 * **The halves of the plan the tab used to leave out entirely.**
 *
 * Six of the eleven keys on `ImportPlan` were rendered nowhere: what counts as a
 * person, where the name comes from and whether a couple heading is split, the
 * details a group sets on every row, the checks that hold a row back, a second
 * sheet attached to the first, and whether somebody who signs in here is matched
 * to their own record. Each is a thing the commit will do, and each is
 * changeable only by asking in the conversation — which is a sentence the office can
 * only act on if they can see what the plan currently says.
 *
 * All read-only, and each says so. The rule §1.7 states is that the *targets*,
 * the *levels* and the *statuses* are the office's to change and the rest is a
 * question about the file: a control that could write half a rule would be worse
 * than a sentence saying where the change is made.
 */
export function PlanSource({ plan }: { plan: ImportPlan }) {
  const rule = plan.source.record_rule;
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_SOURCE_HEAD}</div>
      <div className="act-row">
        <div className="act-name">{RECORD_RULE_LABELS[rule.kind]}</div>
        <div className="act-value fill">
          {joinClauses([
            plan.source.sheet ? `the “${plan.source.sheet}” sheet` : null,
            plan.source.header_row ? `headers on row ${plan.source.header_row}` : null,
            rule.kind === 'row' && rule.requires.length > 0
              ? `and only where ${rule.requires.map(columnName).join(' and ')} says something`
              : null,
          ])}
        </div>
      </div>
    </div>
  );
}

/** Where the name comes from, and whether one heading is two people (§1.16). */
export function PlanIdentity({ plan }: { plan: ImportPlan }) {
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_NAME_HEAD}</div>
      <div className="act-row">
        <div className="act-name">{planNameRuleWords(plan.identity.name)}</div>
        <div className="act-value fill">{NAME_SPLIT_LABELS[plan.identity.split]}</div>
      </div>
      <div className="act-row">
        <div className="act-name">{IMPORT_PLAN_USER_MATCH_HEAD}</div>
        <div className="act-value fill">{USER_MATCH_LABELS[plan.status.user_match]}</div>
      </div>
    </div>
  );
}

/** Details a group or a column writes on every row it catches (§1.13). */
export function PlanRecordFields({ plan }: { plan: ImportPlan }) {
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_RECORD_FIELDS_HEAD}</div>
      {plan.record_field_rules.length === 0 ? (
        <EmptyLine>{IMPORT_PLAN_NO_RECORD_FIELDS}</EmptyLine>
      ) : (
        plan.record_field_rules.map((rule, index) => (
          <div className="act-row" key={`${rule.key}-${index}`}>
            <div className="act-name">{planMatchWords(rule)}</div>
            <div className="act-value fill">
              {humanize(rule.key)}: {String(rule.value)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** Which name checks hold a row back for a look (§1.8). */
export function PlanBin({ plan }: { plan: ImportPlan }) {
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_BIN_HEAD}</div>
      {plan.bin.length === 0 ? (
        <EmptyLine>{IMPORT_PLAN_NO_BIN_RULES}</EmptyLine>
      ) : (
        plan.bin.map((rule, index) => (
          <div className="act-row" key={`${rule.rule}-${index}`}>
            <div className="act-name">{BIN_REASON_LINES[rule.rule]}</div>
            <div className="act-value fill">{rule.detail ?? ''}</div>
          </div>
        ))
      )}
    </div>
  );
}

/**
 * **A second sheet attached to the first** (§2.2's `updates`) — its rows are not
 * people, they are things said about people, and they find their person by an
 * identifier the primary sheet also carries.
 *
 * Its columns get the same table the primary's do, read-only: a joined row is
 * not a record of its own, and the identity and level rules that apply to it are
 * the primary's.
 */
export function PlanJoins({ plan }: { plan: ImportPlan }) {
  const joins = plan.joins ?? [];
  return (
    <div className="plan-block">
      <div className="plan-block-head">{IMPORT_PLAN_JOINS_HEAD}</div>
      {joins.length === 0 ? (
        <EmptyLine>{IMPORT_PLAN_NO_JOINS}</EmptyLine>
      ) : (
        joins.map((join: ImportPlanJoin, index) => (
          <div key={`${join.source.sheet ?? 'join'}-${index}`}>
            <div className="act-row">
              <div className="act-name">{join.source.sheet ?? 'A second sheet'}</div>
              <div className="act-value fill">
                joined where {columnName(join.on.column)} matches {columnName(join.on.matches)} on
                the first
              </div>
            </div>
            <PlanColumns columns={join.columns} editable={false} onChange={() => {}} />
          </div>
        ))
      )}
    </div>
  );
}

/** Which rows a rule catches — a column's value, or a group heading. */
const planMatchWords = (rule: { by: 'column' | 'group'; equals: string }): string =>
  rule.by === 'group' ? `In the group “${rule.equals}”` : `Where the column says “${rule.equals}”`;
